import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import { filenameExtension, formatEcoNumber, formatTimestamp, type ControlledDocument, type DocumentChangeOrder } from "@/lib/documents";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { DocumentModal } from "./DocumentModal";
import { EngineeringChangeMarkdown } from "./EngineeringChangeMarkdown";
import {
  ENGINEERING_CHANGE_DESCRIPTION_TEMPLATE,
  ENGINEERING_CHANGE_IMPLEMENTATION_PLAN_TEMPLATE,
} from "./engineeringChangeOrderTemplate";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

const QUERY_KEY = ["document-change-orders"] as const;

function documentLifecycleLabel(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "wip") return "Work in progress";
  if (status === "draft") return "Draft";
  if (status === "current") return "Released";
  return "Obsolete";
}

function formatDocumentTargets(document: ControlledDocument): string {
  const targets = document.targets ?? [];
  if (targets.length === 0) {
    if (document.sku) return `Part number ${document.sku}`;
    if (document.product_family_code) return `Collection ${document.product_family_code}`;
    return "Targets not set";
  }
  const parts: string[] = [];
  for (const target of targets) {
    if (target.target_type === "sku" && target.sku) parts.push(`Part number ${target.sku}`);
    if (target.target_type === "collection" && target.product_collection_code)
      parts.push(`Collection ${target.product_collection_code}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "Targets not set";
}

function statusClass(status: DocumentChangeOrder["status"]): string {
  if (status === "implemented") return "bg-green-50 text-green-700";
  if (status === "approved") return "bg-blue-50 text-blue-700";
  if (status === "submitted") return "bg-amber-50 text-amber-700";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-700";
}

function markdownSummary(value: string | null): string {
  if (!value) return "No description.";
  const firstParagraph = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line !== "" && !/^#{1,6}\s/.test(line));
  return firstParagraph?.replace(/[*_`]/g, "") ?? "No description.";
}

export function DocumentChangeOrdersPage() {
  const location = useLocation();
  const detailBase = location.pathname.startsWith("/quality/")
    ? "/quality/engineering-changes"
    : "/documents/change-orders";
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("documents");
  const documentsReadOnlyTitle = "Your role has read-only access to the Document Control hub module.";
  const [searchParams, setSearchParams] = useSearchParams();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState(ENGINEERING_CHANGE_DESCRIPTION_TEMPLATE);
  const [implementationPlan, setImplementationPlan] = useState(
    ENGINEERING_CHANGE_IMPLEMENTATION_PLAN_TEMPLATE,
  );
  const [changeReason, setChangeReason] = useState("");
  const [notes, setNotes] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [linkedDocumentId, setLinkedDocumentId] = useState<string | null>(null);

  useEffect(() => {
    const wantsCreate = searchParams.get("create") === "1";
    const documentIdRaw = searchParams.get("documentId");
    const documentIdFromQuery =
      documentIdRaw && documentIdRaw.trim() !== "" ? documentIdRaw.trim() : null;
    if (!wantsCreate) {
      if (documentIdFromQuery) {
        const next = new URLSearchParams(searchParams);
        next.delete("documentId");
        setSearchParams(next, { replace: true });
      }
      return;
    }
    setCreateOpen(true);
    setLinkedDocumentId(documentIdFromQuery);
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    next.delete("documentId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const linkedDocumentQuery = useQuery<{ document: ControlledDocument }, ApiError>({
    queryKey: ["documents", "detail", linkedDocumentId],
    queryFn: () => apiFetch<{ document: ControlledDocument }>(`/api/documents/${linkedDocumentId}`),
    enabled: createOpen && linkedDocumentId != null,
  });

  useEffect(() => {
    if (!linkedDocumentId) return;
    const document = linkedDocumentQuery.data?.document;
    if (!document) return;
    setTitle((previous) => (previous.trim() === "" ? document.title : previous));
  }, [linkedDocumentId, linkedDocumentQuery.data?.document?.id, linkedDocumentQuery.data?.document?.title]);

  const changeOrdersQuery = useQuery<{ changeOrders: DocumentChangeOrder[] }, ApiError>({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<{ changeOrders: DocumentChangeOrder[] }>("/api/document-change-orders"),
  });

  const documentsQuery = useQuery<{ documents: ControlledDocument[] }>({
    queryKey: ["documents", "change-order-documents", expandedId],
    queryFn: () => apiFetch<{ documents: ControlledDocument[] }>("/api/documents", { query: { changeOrderId: expandedId ?? "" } }),
    enabled: expandedId != null,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ changeOrder: DocumentChangeOrder }>("/api/document-change-orders", {
        method: "POST",
        body: {
          title: title.trim(),
          description: description.trim() || null,
          implementationPlan: implementationPlan.trim() || null,
          changeReason: changeReason.trim() || null,
          notes: notes.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["documents", "lookups"] });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
      setTitle("");
      setDescription(ENGINEERING_CHANGE_DESCRIPTION_TEMPLATE);
      setImplementationPlan(ENGINEERING_CHANGE_IMPLEMENTATION_PLAN_TEMPLATE);
      setChangeReason("");
      setNotes("");
      setLinkedDocumentId(null);
      setCreateOpen(false);
    },
  });

  const actionMutation = useMutation({
    mutationFn: (input: { id: string; action: "submit" | "approve" | "implement" | "cancel" }) =>
      apiFetch<{ ok: true }>(`/api/document-change-orders/${input.id}/${input.action}`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
      void queryClient.invalidateQueries({ queryKey: ["documents", "lookups"] });
      if (expandedId) {
        void queryClient.invalidateQueries({ queryKey: ["documents", "change-order-documents", expandedId] });
      }
    },
  });

  const changeOrders = changeOrdersQuery.data?.changeOrders ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageTitleWithHelp hoverHint="Submit, approve, and implement ECOs. Product and document context lives in the workbench.">
            Change queue
          </PageTitleWithHelp>
        </div>
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
        <HoverHintTrigger
          trigger={
            <Button
              type="button"
              variant="primary"
              disabled={!hubWritesAllowed}
              onClick={() => {
                setLinkedDocumentId(null);
                setDescription(ENGINEERING_CHANGE_DESCRIPTION_TEMPLATE);
                setImplementationPlan(ENGINEERING_CHANGE_IMPLEMENTATION_PLAN_TEMPLATE);
                setCreateOpen(true);
              }}
            >
              Create ECO
            </Button>
          }
        >
          Start a new engineering change order to group document revisions and approvals.
        </HoverHintTrigger>
        </ReadOnlyHoverLayer>
      </header>

      {createOpen ? (
        <DocumentModal
          title="Create ECO"
          onClose={() => {
            setCreateOpen(false);
            setLinkedDocumentId(null);
            setDescription(ENGINEERING_CHANGE_DESCRIPTION_TEMPLATE);
            setImplementationPlan(ENGINEERING_CHANGE_IMPLEMENTATION_PLAN_TEMPLATE);
          }}
          showCloseButton={false}
        >
          <div className="space-y-4">
          {linkedDocumentId ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Linked document</div>
              {linkedDocumentQuery.isPending ? (
                <p className="mt-2 text-slate-600">Loading document…</p>
              ) : linkedDocumentQuery.isError ? (
                <p className="mt-2 text-red-600">{(linkedDocumentQuery.error as Error).message}</p>
              ) : linkedDocumentQuery.data?.document ? (
                <div className="mt-2 space-y-1">
                  <div className="font-medium text-slate-900">{linkedDocumentQuery.data.document.title}</div>
                  <div className="text-slate-600">
                    Revision {linkedDocumentQuery.data.document.revision} ·{" "}
                    {linkedDocumentQuery.data.document.document_kind_label} ·{" "}
                    {filenameExtension(linkedDocumentQuery.data.document.original_filename) ?? "—"} file ·{" "}
                    {documentLifecycleLabel(linkedDocumentQuery.data.document.lifecycle_status)}
                  </div>
                  <div className="text-slate-600">{formatDocumentTargets(linkedDocumentQuery.data.document)}</div>
                  {linkedDocumentQuery.data.document.change_order_number ? (
                    <p className="mt-2 text-amber-900">
                      This revision is already on engineering change order{" "}
                      {linkedDocumentQuery.data.document.change_order_number}. Creating a new order does not move the file;
                      attach drafts under the correct order from the workbench upload flow.
                    </p>
                  ) : null}
                  <p className="text-xs text-slate-500">Record id {linkedDocumentQuery.data.document.id}</p>
                </div>
              ) : null}
            </div>
          ) : null}
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle} layout="block">
          <fieldset
            disabled={!hubWritesAllowed || createMutation.isPending}
            className="border-0 p-0"
          >
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700 md:col-span-2">
              Title
              <Input className="mt-1" value={title} onChange={(event) => setTitle(event.target.value)} />
            </label>
            <label className="block text-sm font-medium text-slate-700 md:col-span-2">
              Description (Markdown)
              <textarea
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 font-mono text-sm text-slate-900 shadow-sm"
                rows={18}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>
            {description.trim() ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Description preview
                </div>
                <EngineeringChangeMarkdown>{description}</EngineeringChangeMarkdown>
              </div>
            ) : null}
            <label className="block text-sm font-medium text-slate-700 md:col-span-2">
              Implementation plan (Markdown, SGS only — not on ECNs)
              <textarea
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 font-mono text-sm text-slate-900 shadow-sm"
                rows={8}
                value={implementationPlan}
                onChange={(event) => setImplementationPlan(event.target.value)}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 md:col-span-2">
              Change reason
              <textarea
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                rows={2}
                value={changeReason}
                onChange={(event) => setChangeReason(event.target.value)}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 md:col-span-2">
              Notes
              <textarea
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                rows={2}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>
          </div>
          </fieldset>
          </ReadOnlyHoverLayer>
          {createMutation.isError ? (
            <p className="text-sm text-red-600">{(createMutation.error as Error).message}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <HoverHintTrigger
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  disabled={createMutation.isPending}
                  onClick={() => {
                    setCreateOpen(false);
                    setLinkedDocumentId(null);
                    setDescription(ENGINEERING_CHANGE_DESCRIPTION_TEMPLATE);
                    setImplementationPlan(ENGINEERING_CHANGE_IMPLEMENTATION_PLAN_TEMPLATE);
                  }}
                >
                  Cancel
                </Button>
              }
            >
              Close without creating an engineering change order.
            </HoverHintTrigger>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
            <HoverHintTrigger
              trigger={
                <Button
                  type="button"
                  variant="primary"
                  disabled={
                    createMutation.isPending || title.trim() === "" || !hubWritesAllowed
                  }
                  onClick={() => createMutation.mutate()}
                >
                  Create ECO
                </Button>
              }
            >
              Create the engineering change order. The next sequential ECO number is assigned automatically.
            </HoverHintTrigger>
            </ReadOnlyHoverLayer>
          </div>
          </div>
        </DocumentModal>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>ECO queue</CardTitle>
        </CardHeader>
        <CardBody>
          {changeOrdersQuery.isError ? (
            <p className="text-sm text-red-600">{changeOrdersQuery.error.message}</p>
          ) : changeOrdersQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : changeOrders.length === 0 ? (
            <p className="text-sm text-slate-500">No change orders yet.</p>
          ) : (
            <div className="space-y-3">
              {changeOrders.map((changeOrder) => (
                <section key={changeOrder.id} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-semibold text-slate-900">
                          <Link
                            className="text-brand-700 hover:underline"
                            to={`${detailBase}/${changeOrder.id}`}
                          >
                            {formatEcoNumber(changeOrder.number)} - {changeOrder.title}
                          </Link>
                        </h2>
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(changeOrder.status)}`}>
                          {changeOrder.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-600">{markdownSummary(changeOrder.description)}</p>
                      {changeOrder.engineering_change_notice ? (
                        <p className="mt-1 text-xs text-slate-500">ECN {changeOrder.engineering_change_notice}</p>
                      ) : null}
                      {changeOrder.change_reason ? (
                        <p className="mt-1 text-sm text-slate-600">{changeOrder.change_reason}</p>
                      ) : null}
                      <p className="mt-1 text-xs text-slate-500">
                        {changeOrder.document_count} document(s), created {formatTimestamp(changeOrder.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <HoverHintTrigger
                        trigger={
                          <Link to="/documents">
                            <Button type="button" size="sm" variant="ghost">
                              Open workbench
                            </Button>
                          </Link>
                        }
                      >
                        Open Document Control Workbench to attach or manage draft documents for this change order.
                      </HoverHintTrigger>
                      <HoverHintTrigger
                        trigger={
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedId(expandedId === changeOrder.id ? null : changeOrder.id)}
                          >
                            {expandedId === changeOrder.id ? "Hide documents" : "Show documents"}
                          </Button>
                        }
                      >
                        Toggle the list of draft documents currently linked to this engineering change order.
                      </HoverHintTrigger>
                      {changeOrder.status === "draft" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: changeOrder.id, action: "submit" })}
                            >
                              Submit
                            </Button>
                          }
                        >
                          Send this draft engineering change order for review (moves to submitted).
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {changeOrder.status === "submitted" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: changeOrder.id, action: "approve" })}
                            >
                              Approve
                            </Button>
                          }
                        >
                          Record approval so the change order can be implemented when ready.
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {changeOrder.status === "approved" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: changeOrder.id, action: "implement" })}
                            >
                              Implement
                            </Button>
                          }
                        >
                          Release attached draft documents and close the engineering change order workflow.
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {["draft", "submitted", "approved"].includes(changeOrder.status) ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: changeOrder.id, action: "cancel" })}
                            >
                              Cancel
                            </Button>
                          }
                        >
                          Cancel this engineering change order when it should not proceed.
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </div>
                  </div>
                  {expandedId === changeOrder.id ? (
                    <div className="mt-4 rounded-md bg-slate-50 p-3">
                      {documentsQuery.isLoading ? (
                        <p className="text-sm text-slate-500">Loading documents...</p>
                      ) : (documentsQuery.data?.documents ?? []).length === 0 ? (
                        <p className="text-sm text-slate-500">No draft documents attached.</p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {(documentsQuery.data?.documents ?? []).map((document) => {
                            const extension = filenameExtension(document.original_filename);
                            return (
                            <li key={document.id} className="flex flex-wrap justify-between gap-3">
                              <span>
                                {document.title} rev {document.revision} ({document.lifecycle_status})
                                {document.engineering_change_notice ? ` - ECN ${document.engineering_change_notice}` : ""}
                              </span>
                              <span className="text-slate-500">
                                {document.document_kind_label}
                                {extension ? (
                                  <>
                                    {" "}
                                    · {extension}
                                  </>
                                ) : null}
                              </span>
                            </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </section>
              ))}
            </div>
          )}
          {actionMutation.isError ? (
            <p className="mt-3 text-sm text-red-600">{(actionMutation.error as Error).message}</p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

